import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';

import {
  deleteCustomerProduct,
  fetchCustomerProduct,
  fetchCustomerProducts,
  patchCustomerProduct,
  postCustomerProduct,
} from '@sophocles/api/customerProduct';
import {
  deleteProduct,
  fetchProduct,
  fetchProducts,
  patchProduct,
  patchProductPicture,
  postProduct,
} from '@sophocles/api/product';
import {
  deleteProductBom,
  fetchProductBom,
  fetchProductBoms,
  patchProductBom,
  postProductBom,
} from '@sophocles/api/productBom';
import {
  deleteProductInventory,
  fetchProductInventories,
  fetchProductInventory,
  patchProductInventory,
  postProductInventory,
} from '@sophocles/api/productInventory';
import {
  deleteProductInventoryLog,
  fetchProductInventoryLog,
  fetchProductInventoryLogs,
  patchProductInventoryLog,
  postProductInventoryLog,
} from '@sophocles/api/productInventoryLog';
import { deleteVendor, fetchVendor, fetchVendors, patchVendor, postVendor } from '@sophocles/api/vendor';
import {
  deleteVendorPart,
  fetchVendorPart,
  fetchVendorParts,
  patchVendorPart,
  postVendorPart,
} from '@sophocles/api/vendorPart';
import { createCrudThunks } from '@sophocles/features/warehouse/crudThunks';
import {
  CustomerProductSchema,
  ProductBomSchema,
  ProductInventoryLogSchema,
  ProductInventorySchema,
  ProductSchema,
  VendorPartSchema,
  VendorSchema,
} from '@sophocles/features/warehouse/products/schema';

// Vendor Thunks
const vendorThunks = createCrudThunks(
  'warehouse/vendors',
  {
    fetchMany: fetchVendors,
    fetchOne: fetchVendor,
    create: postVendor,
    patch: patchVendor,
    remove: deleteVendor,
  },
  data => normalize(data, VendorSchema),
);
export const loadVendors = vendorThunks.loadMany;
export const loadVendor = vendorThunks.loadOne;
// DEPRECATED(grid-legacy-cleanup): create/edit/remove kept only so vendorSlice's
// createEntitySlice extraReducers compile — no UI dispatches them.
export const createVendor = vendorThunks.create;
export const editVendor = vendorThunks.edit;
export const removeVendor = vendorThunks.remove;

// VendorPart Thunks
const vendorPartThunks = createCrudThunks(
  'warehouse/vendorParts',
  {
    fetchMany: fetchVendorParts,
    fetchOne: fetchVendorPart,
    create: postVendorPart,
    patch: patchVendorPart,
    remove: deleteVendorPart,
  },
  data => normalize(data, VendorPartSchema),
);
export const loadVendorParts = vendorPartThunks.loadMany;
export const loadVendorPart = vendorPartThunks.loadOne;
// DEPRECATED(grid-legacy-cleanup): create/edit/remove kept only so vendorPartSlice's
// createEntitySlice extraReducers compile — no UI dispatches them.
export const createVendorPart = vendorPartThunks.create;
export const editVendorPart = vendorPartThunks.edit;
export const removeVendorPart = vendorPartThunks.remove;

// CustomerProduct Thunks
const customerProductThunks = createCrudThunks(
  'warehouse/customerProducts',
  {
    fetchMany: fetchCustomerProducts,
    fetchOne: fetchCustomerProduct,
    create: postCustomerProduct,
    patch: patchCustomerProduct,
    remove: deleteCustomerProduct,
  },
  data => normalize(data, CustomerProductSchema),
);
export const loadCustomerProducts = customerProductThunks.loadMany;
export const loadCustomerProduct = customerProductThunks.loadOne;
// DEPRECATED(grid-legacy-cleanup): create/edit/remove kept only so
// customerProductSlice's createEntitySlice extraReducers compile — no UI dispatches them.
export const createCustomerProduct = customerProductThunks.create;
export const editCustomerProduct = customerProductThunks.edit;
export const removeCustomerProduct = customerProductThunks.remove;

// Product Thunks — reads stay here; write surface migrated to RTK Query (see features/entities/products.js).
const productThunks = createCrudThunks('warehouse/products', {
  fetchMany: fetchProducts,
  fetchOne: fetchProduct,
});
export const loadProducts = productThunks.loadMany;
export const loadProduct = productThunks.loadOne;

// DEPRECATED(grid-legacy-cleanup): write thunks kept only so productSlice's createEntitySlice extraReducers
// compile. New code should use useCreateProductMutation / usePatchProductMutation / useRemoveProductMutation
// from @sophocles/features/entities/products instead.
export const createProduct = createAsyncThunk('warehouse/products/create', async (payload, { rejectWithValue }) => {
  try {
    const data = await postProduct(payload);
    return normalize(data, ProductSchema);
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});

export const editProduct = createAsyncThunk(
  'warehouse/products/patch',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      const data = await patchProduct(id, payload);
      return normalize(data, ProductSchema);
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const updateProductPicture = createAsyncThunk(
  'warehouse/products/updatePicture',
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const data = await patchProductPicture(id, file);
      return normalize(data, ProductSchema);
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  },
);

export const removeProduct = createAsyncThunk('warehouse/products/delete', async (productId, { rejectWithValue }) => {
  try {
    await deleteProduct(productId);
    return productId;
  } catch (e) {
    return rejectWithValue(e.response.data);
  }
});

// ProductBom Thunks
const productBomThunks = createCrudThunks(
  'warehouse/productBoms',
  {
    fetchMany: fetchProductBoms,
    fetchOne: fetchProductBom,
    create: postProductBom,
    patch: patchProductBom,
    remove: deleteProductBom,
  },
  data => normalize(data, ProductBomSchema),
);
export const loadProductBoms = productBomThunks.loadMany;
export const loadProductBom = productBomThunks.loadOne;
// DEPRECATED(grid-legacy-cleanup): create/edit/remove kept only so productBomSlice's
// createEntitySlice extraReducers compile — no UI dispatches them.
export const createProductBom = productBomThunks.create;
export const editProductBom = productBomThunks.edit;
export const removeProductBom = productBomThunks.remove;

// ProductInventory Thunks
const productInventoryThunks = createCrudThunks(
  'warehouse/productInventories',
  {
    fetchMany: fetchProductInventories,
    fetchOne: fetchProductInventory,
    create: postProductInventory,
    patch: patchProductInventory,
    remove: deleteProductInventory,
  },
  data => normalize(data, ProductInventorySchema),
);
export const loadProductInventories = productInventoryThunks.loadMany;
export const loadProductInventory = productInventoryThunks.loadOne;
// DEPRECATED(grid-legacy-cleanup): kept only so productInventorySlice's
// createEntitySlice extraReducers compile — no UI dispatches this create.
export const createProductInventory = productInventoryThunks.create;
export const editProductInventory = productInventoryThunks.edit;
// DEPRECATED(grid-legacy-cleanup): kept only so productInventorySlice's
// createEntitySlice extraReducers compile — no UI dispatches this remove.
export const removeProductInventory = productInventoryThunks.remove;

// ProductInventoryLog Thunks
const productInventoryLogThunks = createCrudThunks(
  'warehouse/productInventoryLogs',
  {
    fetchMany: fetchProductInventoryLogs,
    fetchOne: fetchProductInventoryLog,
    create: postProductInventoryLog,
    patch: patchProductInventoryLog,
    remove: deleteProductInventoryLog,
  },
  data => normalize(data, ProductInventoryLogSchema),
);
export const loadProductInventoryLogs = productInventoryLogThunks.loadMany;
export const loadProductInventoryLog = productInventoryLogThunks.loadOne;
// DEPRECATED(grid-legacy-cleanup): create/edit/remove kept only so
// productInventoryLogSlice's createEntitySlice extraReducers compile — no UI dispatches them.
export const createProductInventoryLog = productInventoryLogThunks.create;
export const editProductInventoryLog = productInventoryLogThunks.edit;
export const removeProductInventoryLog = productInventoryLogThunks.remove;
